<template>
  <input
    autocomplete="off"
    :readonly="forprint"
    class="rx-input"
    :name="name"
    ref="input"
    v-validate="myValidation"
    v-bind:value="value"
    v-on:input="updateValue($event.target.value)"
    v-on:focus="selectAllAndSaveValue"
    v-on:blur="formatValue(decimalEnabled)"
  >
</template>

<script>
function empty(testValue) {
  return (
    testValue === null ||
    testValue === undefined ||
    (typeof testValue === "string" && testValue.trim() === "")
  );
}

export default {
  inject: ["$validator"],
  props: {
    value: {},
    label: {
      type: String,
      default: ""
    },
    precision: {
      type: Number,
      default: 1
    },
    name: {
      type: String
    },
    useSign: {
      type: Boolean,
      default: false
    },
    min: {
      type: Number
    },
    max: {
      type: Number
    },
    required: {
      type: Boolean,
      default: false
    },
    forprint: {
      type: Boolean,
      default: false
    },
    decimalEnabled: {
      type: Boolean,
      default: true
    }
  },

  data() {
    return {
      previousValue: null,
      proposedValue: null
    };
  },

  mounted() {
    this.saveValue();
    this.proposedValue = this.value;
  },

  computed: {
    myValidation() {
      const rules = [];
      if (this.required) rules.push("required");
      if (this.max != null) rules.push("max_value:" + this.max);
      if (this.min != null) rules.push("min_value:" + this.min);
      return rules.join("|");
    },

    isPantoField() {
      // Matches 'Panto', 'PantoA', 'PantoB', 'PantoC', etc.
      return this.name != null && this.name.toLowerCase().startsWith("panto");
    },
  },

  methods: {
    emitChange(newValue) {
      this.$refs.input.value = newValue;
      this.$emit("input", newValue);
    },

    updateValue(value) {
      this.proposedValue = value;
    },

    formatValue(decimalEnabled) {
      if (empty(this.proposedValue)) {
        this.emitChange(null);
        return;
      }

      let temp   = String(this.proposedValue).trim();
      const signed = temp[0] === "-" || temp[0] === "+";

      if (temp[0] === "+") temp = temp.substring(1);
      if (signed && !this.useSign) temp = temp.substring(1);

      let formattedValue = decimalEnabled
        ? parseFloat(temp).toFixed(this.precision)
        : Math.round(parseFloat(temp)).toString();

      if (this.isPantoField) {
        // ── Panto-specific handling ──────────────────────────────────────────
        // Strip any leading '+' for display (Panto values show without sign).
        if (Number(formattedValue) > 0) {
          formattedValue = String(formattedValue);
        }

        const min = -5;
        const max = 25;

        if (Number(formattedValue) >= min && Number(formattedValue) <= max) {
          // Valid: clear only THIS field's error.
          // RxInfo.vue is responsible for clearing stale errors from other
          // panto variants when the design changes (via previousPantoFieldName).
          this.$validator.errors.remove(this.name);
        } else {
          this.$validator.errors.add({
            field: this.name,
            msg: `${this.name} must be between ${min} and ${max}`,
          });
        }
        // Always emit — even out-of-range values are emitted so the parent
        // can decide how to handle them.
        this.emitChange(formattedValue);
        return;
      }

      // ── Generic non-Panto handling ─────────────────────────────────────────
      if (this.useSign && Number(formattedValue) > 0) {
        formattedValue = "+" + formattedValue;
      }

      this.$validator.errors.remove(this.name);

      if (this.min != null && Number(formattedValue) < this.min) {
        this.$validator.errors.add({
          field: this.name,
          msg: `${this.name} must be greater than or equal to ${this.min}`,
        });
      } else if (this.max != null && Number(formattedValue) > this.max) {
        this.$validator.errors.add({
          field: this.name,
          msg: `${this.name} must be less than or equal to ${this.max}`,
        });
      }

      this.emitChange(formattedValue);
    },

    saveValue() {
      this.previousValue = this.value;
    },

    selectAllAndSaveValue(event) {
      this.saveValue();
      event.target.select();
    },
  },
};
</script>

<style>
.rx-input {
  font-size: 10pt;
  display: table-cell;
  width: 100%;
  height: 100%;
  margin: 0;
}
</style>
